import {
  Module,
  VuexModule,
  Action,
  Mutation,
  getModule
} from 'vuex-module-decorators'
import Vue from 'vue'
import axios from 'axios'

import store from '@/store'
import dashboardModule from '@/store/dashboard'
import listModule from '@/store/list'
import whitelabelModule from '@/store/whitelabel'
import { Card, CardList } from '@/types/Card'
import { DefaultCardList } from '@/utils/enums'
import { Whitelabel } from '@/types/Whitelabel'

const name = 'card'

@Module({
  namespaced: true,
  dynamic: true,
  name,
  store
})
class CardModule extends VuexModule {
  private currentCard?: Card
  private allCards: Card[] = []
  private unfilteredCards: Card[] = []
  private featuredCardList?: CardList[]
  private currentCardList?: CardList
  private allCardLists: CardList[] = []

  private get whitelabel(): Whitelabel {
    return whitelabelModule.whitelabel
  }

  get card(): Card | undefined {
    return this.currentCard
  }

  get cards(): Card[] {
    return this.allCards
  }

  get cardsUnfiltered(): Card[] {
    return this.unfilteredCards
  }

  get cardList(): CardList | undefined {
    return this.currentCardList
  }

  get cardLists(): CardList[] {
    return this.allCardLists
  }

  get featuredList(): CardList[] | undefined {
    return this.featuredCardList
  }

  @Mutation
  setCurrentCard(payload: Card) {
    Vue.set(this, 'currentCard', payload)
  }

  @Mutation
  setAllCards(payload: Card[]) {
    Vue.set(this, 'allCards', payload)
  }

  @Mutation
  setUnfilteredCards(payload: Card[]) {
    Vue.set(this, 'unfilteredCards', payload)
  }

  @Mutation
  setCurrentCardList(payload: CardList) {
    Vue.set(this, 'currentCardList', payload)
  }

  @Mutation
  setAllCardLists(payload: CardList[]) {
    Vue.set(this, 'allCardLists', payload)
  }

  @Mutation
  setFeaturedCardList(payload: CardList[]) {
    Vue.set(this, 'featuredCardList', payload)
  }

  @Action({ rawError: true })
  async fetch(id: string) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/card/${id}`
      )
      await this.context.commit('setCurrentCard', data)
    } catch (error) {
      dashboardModule.newError('Karte konnte nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchAll(params: {
    limit: number
    q?: string
    page?: number
    lean?: boolean
    sortBy?: string
    sortDesc?: boolean
  }) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/card/list`,
        {
          params
        }
      )
      this.context.commit('setAllCards', data.cards)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Karten konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchUnfiltered() {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/card/list`,
        {
          params: {
            limit: 'null'
          }
        }
      )
      this.context.commit('setUnfilteredCards', data.cards)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Karten konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async create(payload: { whitelabel: string; card: Partial<Card> }) {
    try {
      const { data } = await axios.post(
        `/admin/${payload.whitelabel}/card`,
        payload.card
      )
      this.context.commit('setCurrentCard', data)
      this.fetchAll({ limit: listModule.perPage })
      this.fetchUnfiltered()
      dashboardModule.newMessage('Karte wurde erfolgreich erstellt')
    } catch (error) {
      dashboardModule.newError('Karte konnte nicht erstellt werden')
    }
  }

  @Action({ rawError: true })
  async update(payload: { whitelabel: string; card: Partial<Card> }) {
    try {
      const { data } = await axios.put(
        `/admin/${payload.whitelabel}/card/${payload.card._id}`,
        payload.card
      )
      this.context.commit('setCurrentCard', data.card)
      dashboardModule.newMessage('Karte wurde erfolgreich aktualisiert')
    } catch (error) {
      dashboardModule.newError('Karte konnte nicht aktualisiert werden')
    }
  }

  @Action({ rawError: true })
  async fetchCardList(id: string) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/cardlist/${id}`
      )
      await this.context.commit('setCurrentCardList', data)
    } catch (error) {
      dashboardModule.newError('Karten-Liste konnte nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchAllCardLists(params: {
    limit: number
    q?: string
    page?: number
    sortBy?: string
    sortDesc?: boolean
  }) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/cardlist/list`,
        {
          params
        }
      )
      this.context.commit('setAllCardLists', data.cardlists)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Karten-Listen konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchFeaturedList() {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/cardList/list`,
        {
          params: {
            q: DefaultCardList.NAME
          }
        }
      )
      await this.context.commit('setFeaturedCardList', data.cardlists)
    } catch (error) {
      dashboardModule.newError(
        'Featured Karten-Liste konnte nicht geladen werden'
      )
    }
  }

  @Action({ rawError: true })
  async createCardList(payload: {
    whitelabel: string
    cardList: Partial<CardList>
  }) {
    try {
      const { data } = await axios.post(
        `/admin/${payload.whitelabel}/cardlist`,
        payload.cardList
      )
      this.context.commit('setCurrentCardList', data)
      this.fetchAllCardLists({ limit: listModule.perPage })
      dashboardModule.newMessage('Karten-Liste wurde erfolgreich erstellt')
    } catch (error) {
      dashboardModule.newError('Karten-Liste konnte nicht erstellt werden')
    }
  }

  @Action({ rawError: true })
  async updateCardList(payload: {
    whitelabel: string
    cardList: Partial<CardList>
  }) {
    try {
      const { data } = await axios.put(
        `/admin/${payload.whitelabel}/cardlist/${payload.cardList._id}`,
        payload.cardList
      )
      this.context.commit('setCurrentCardList', data.cardlist)
      dashboardModule.newMessage('Karten-Liste wurde erfolgreich aktualisiert')
    } catch (error) {
      dashboardModule.newError('Karten-Liste konnte nicht aktualisiert werden')
    }
  }

  @Action({ rawError: true })
  async rearrangeActiveLists(payload: {
    whitelabel: string
    cardLists: string[]
  }) {
    try {
      await axios.put(
        `/admin/${payload.whitelabel}/cardlist/list/rearrange`,
        payload.cardLists
      )
      dashboardModule.newMessage(
        'Karten-Listen wurden erfolgreich aktualisiert'
      )
    } catch (error) {
      dashboardModule.newError(
        'Karten-Listen konnten nicht aktualisiert werden'
      )
    }
  }

  @Action({ rawError: true })
  setCard(payload: Card) {
    this.context.commit('setCurrentCard', payload)
  }

  @Action({ rawError: true })
  setCards(payload: Card[]) {
    this.context.commit('setAllCards', payload)
  }

  @Action({ rawError: true })
  setCardList(payload: CardList) {
    this.context.commit('setCurrentCardList', payload)
  }

  @Action({ rawError: true })
  setCardLists(payload: CardList[]) {
    this.context.commit('setAllCardList', payload)
  }
}

export default getModule(CardModule)
